import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute - Wraps routes that require authentication.
 *
 * Uses useAuth() to check authentication state from AuthContext.
 * If not authenticated, redirects to /login with the current location
 * saved in state so the user can be redirected back after login.
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Wait for the auth probe (GET /api/auth/me) to finish before deciding.
  // Without this, a page reload would see isAuthenticated=false momentarily
  // and redirect to /login before the cookie-based session is verified.
  //
  // Show a small, scoped loader (NOT a full-screen block — that was removed in
  // Phase 28.1). Public routes already rendered instantly; only this protected
  // branch waits, so a returning user on a cold backend gets clear feedback
  // instead of a blank screen.
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-pp-bg" role="status">
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pp-border border-t-pp-accent" />
          <span className="text-[13px] text-pp-muted">Loading your dashboard…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

export default ProtectedRoute;
